import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gantt, Tooltip, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/all.css';
import { Alert, Button, Modal } from '@/components/ui';
import {
  getScheduleResources,
  getScheduleVehicles,
  type ScheduleResource,
  type ScheduleVehicle,
} from '../../../services/scheduleService';
import { taskService } from '../../../services/quantifiedTaskService';
import { startOfDay, addDays, format as formatDate, parseISO } from 'date-fns';
import {
  resourcesToSvarTasks,
  parseDateOnly,
  resolveDragEndRange,
  toSvarTask,
  SYNTHETIC_ID_START,
  type SvarTask,
  type PlannedDatesMap,
} from '../utils/scheduleGanttMapping';
import type { IApi } from '@svar-ui/react-gantt';
import { ScheduleTaskTooltipContent } from './ScheduleTaskTooltip';
import type { FillerActivity } from '../SchedulePage';
import {
  buildPrintDependencyArrowInitScript,
  escapeHtml,
  formatDependencyNotes,
  stripScheduleIconPrefix,
} from '../utils/schedulePrint';
import { buildSvarDependencyLinks } from '../utils/scheduleDependencyLinks';
import {
  buildVehicleResources,
  filterResourcesByTaskFilters,
  normalizeScheduleFilterValues,
  parseServiceAreaFilterIds,
  toScheduleListParam,
} from '../utils/scheduleResourceAggregation';
import { computeScheduleGanttTimeline } from '../utils/scheduleGanttTimeline';
import {
  computeTodayMarkerOffsetInScalePx,
  SCHEDULE_GANTT_TIMELINE_CELL_WIDTH,
} from '../utils/scheduleGanttTodayLineLayout';
import { sortTasksByCompletionDependency } from '../utils/scheduleTaskDependencyOrder';
import {
  getScheduleGanttHorizontalScrollTarget,
  useScheduleGanttChartScroll,
} from '../hooks/useScheduleGanttChartScroll';
import type { ScheduleTimelineScale } from './ScheduleTimelineScaleSwitcher';

export type { PlannedDatesMap } from '../utils/scheduleGanttMapping';

type SvarTaskClickEvent =
  | number
  | string
  | {
      id?: number | string;
      task?: { id?: number | string };
    };

function resolveSyntheticTaskIdFromEvent(ev: SvarTaskClickEvent): number | null {
  if (typeof ev === 'number') return Number.isFinite(ev) ? ev : null;
  if (typeof ev === 'string') {
    const parsed = Number.parseInt(ev, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (ev && typeof ev === 'object') {
    const rawId = ev.id ?? ev.task?.id;
    if (typeof rawId === 'number') return Number.isFinite(rawId) ? rawId : null;
    if (typeof rawId === 'string') {
      const parsed = Number.parseInt(rawId, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
  }
  return null;
}

/** Returns new schedule data with the given task's planned dates updated (immutable). */
function updateTaskDatesInScheduleData(
  data: ScheduleResource[] | null,
  taskId: number,
  identifiedDate: string,
  plannedCompletionDate: string
): ScheduleResource[] | null {
  if (!data || !Array.isArray(data)) return data;
  return data.map((resource) => {
    const tasks = Array.isArray(resource.tasks) ? resource.tasks : [];
    const hasChange = tasks.some((t) => t.id === taskId);
    if (!hasChange) return resource;
    return {
      ...resource,
      tasks: tasks.map((t) =>
        t.id === taskId
          ? { ...t, identifiedDate, plannedCompletionDate }
          : t
      ),
    };
  });
}

interface ResourceGanttChartProps {
  /** Structural subset of `ScheduleResourcePageFilters`; every dropdown field is a multiselect. */
  filters: {
    serviceAreas: string[];
    foremanAreas: string[];
    equipmentTypes: string[];
    projectIds: number[];
    activityIds: number[];
    activityNumbers: string[];
    statuses: string[];
    startDate: string;
    endDate: string;
    workTypes: string[];
    taskSearch: string;
  };
  scheduleData: ScheduleResource[] | null;
  setScheduleData: (data: ScheduleResource[] | null) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
  groupBy?: 'employee' | 'vehicle';
  fillerActivities?: FillerActivity[];
  foremanAreas?: Array<{ id: number; name: string; serviceAreaId: number; serviceAreaName: string }>;
  defaultContractYear?: string;
  scalePreset: ScheduleTimelineScale;
}

interface ScheduleConflictDetail {
  resourceName: string;
  taskId: number;
  taskName: string;
  startDate: string;
  endDate: string;
}

interface PendingScheduleConflict {
  sourceTaskId: number;
  identifiedDateStr: string;
  plannedCompletionDateStr: string;
  startDate: Date;
  endDate: Date;
  taskLabel: string;
  resourceConflicts: ScheduleConflictDetail[];
  assignedResourceCount: number;
}

interface ExistingScheduleConflictNotice {
  conflictTaskCount: number;
  overloadedRowCount: number;
}

interface EquipmentCapacityConflict {
  serviceArea: string;
  vehicleType: string;
  available: number;
  required: number;
}

interface VehicleCapacityAnalysis {
  conflicts: EquipmentCapacityConflict[];
  conflictTaskIds: Set<number>;
  overloadedRowCount: number;
}

function formatSummaryPlannedDates({
  plannedStartDates,
  plannedCompletionDates,
}: {
  plannedStartDates: Date[];
  plannedCompletionDates: Date[];
}): { plannedStartDate: string | null; plannedCompletionDate: string | null } {
  const plannedStartDate =
    plannedStartDates.length > 0
      ? formatDate(new Date(Math.min(...plannedStartDates.map((d) => d.getTime()))), 'yyyy-MM-dd')
      : null;
  const plannedCompletionDate =
    plannedCompletionDates.length > 0
      ? formatDate(new Date(Math.max(...plannedCompletionDates.map((d) => d.getTime()))), 'yyyy-MM-dd')
      : null;
  return { plannedStartDate, plannedCompletionDate };
}

function dateRangesOverlap(startA: Date, endA: Date, startB: Date, endB: Date): boolean {
  return startA.getTime() <= endB.getTime() && startB.getTime() <= endA.getTime();
}

function parseTaskStartDate(
  task: ScheduleResource['tasks'][number],
  chartRange?: { start: Date; end: Date }
): Date | null {
  if (task.yearRoundSchedule && chartRange) {
    return startOfDay(chartRange.start);
  }
  return parseDateOnly(task.identifiedDate ?? task.startDate ?? '') ?? null;
}

function parseTaskEndDate(
  task: ScheduleResource['tasks'][number],
  fallbackStart: Date,
  chartRange?: { start: Date; end: Date }
): Date {
  if (task.yearRoundSchedule && chartRange) {
    return startOfDay(chartRange.end);
  }
  const plannedEnd = parseDateOnly(task.plannedCompletionDate ?? '');
  if (plannedEnd && plannedEnd.getTime() >= fallbackStart.getTime()) {
    return plannedEnd;
  }
  return fallbackStart;
}

function collectConflictTaskIds(
  resources: ScheduleResource[],
  chartRange: { start: Date; end: Date }
): { conflictTaskIds: Set<number>; overloadedRowCount: number } {
  const conflictTaskIds = new Set<number>();
  const taskToRows = new Map<number, Set<number>>();
  let overloadedRowCount = 0;

  for (const resource of resources) {
    const resourceTasks = Array.isArray(resource.tasks) ? resource.tasks : [];
    let rowHasOverlap = false;
    for (const task of resourceTasks) {
      if (!taskToRows.has(task.id)) taskToRows.set(task.id, new Set<number>());
      taskToRows.get(task.id)?.add(resource.id);
    }
    for (let i = 0; i < resourceTasks.length; i += 1) {
      const left = resourceTasks[i];
      const leftStart = parseTaskStartDate(left, chartRange);
      if (!leftStart) continue;
      const leftEnd = parseTaskEndDate(left, leftStart, chartRange);
      for (let j = i + 1; j < resourceTasks.length; j += 1) {
        const right = resourceTasks[j];
        const rightStart = parseTaskStartDate(right, chartRange);
        if (!rightStart) continue;
        const rightEnd = parseTaskEndDate(right, rightStart, chartRange);
        if (!dateRangesOverlap(leftStart, leftEnd, rightStart, rightEnd)) continue;
        rowHasOverlap = true;
        conflictTaskIds.add(left.id);
        conflictTaskIds.add(right.id);
      }
    }
    if (rowHasOverlap) overloadedRowCount += 1;
  }

  taskToRows.forEach((rowIds, taskId) => {
    if (rowIds.size > 1) conflictTaskIds.add(taskId);
  });

  return { conflictTaskIds, overloadedRowCount };
}

/**
 * Vehicle mode hierarchy (type-only, no unit names):
 * - Level 1: vehicleType in service area (summary row)
 * - Level 2: planned/active/overdue task bars (child rows)
 */
function vehicleUnitsToSvarTasks(units: ScheduleResource[], rangeStart: Date, rangeEnd: Date): SvarTask[] {
  const rows = Array.isArray(units) ? units : [];
  const sortedRows = rows
    .slice()
    .sort((a, b) =>
      `${a.name} ${a.position}`.localeCompare(`${b.name} ${b.position}`)
    );
  let nextSyntheticId = SYNTHETIC_ID_START;
  const svarTasks: SvarTask[] = [];

  for (const row of sortedRows) {
    const typeRowId = nextSyntheticId++;
    const rowTasks = sortTasksByCompletionDependency(Array.isArray(row.tasks) ? row.tasks : []);
    const childTasks: SvarTask[] = [];

    for (const task of rowTasks) {
      const taskRowId = nextSyntheticId++;
      const mapped = toSvarTask(
        task,
        taskRowId,
        typeRowId,
        row.foremanArea?.name ?? undefined,
        row.position ?? undefined,
        rangeStart,
        rangeEnd
      );
      if (mapped) childTasks.push(mapped);
    }

    // Build exact occupied segments from raw task planned dates (not mapped bars),
    // so parent occupancy reflects true assigned days and preserves gaps.
    const chartRange = { start: rangeStart, end: rangeEnd };
    const occupiedSegments = rowTasks
      .map((task) => {
        if (task.yearRoundSchedule) {
          return {
            start: startOfDay(rangeStart),
            end: startOfDay(rangeEnd),
          };
        }
        const start = parseTaskStartDate(task, chartRange);
        if (!start) return null;
        const end = parseTaskEndDate(task, start, chartRange);
        return {
          start: startOfDay(start),
          end: startOfDay(end),
        };
      })
      .filter((seg): seg is { start: Date; end: Date } => seg != null)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .reduce<Array<{ start: Date; end: Date }>>((acc, cur) => {
        if (acc.length === 0) {
          acc.push({ ...cur });
          return acc;
        }
        const last = acc[acc.length - 1];
        const contiguousOrOverlap = cur.start.getTime() <= addDays(last.end, 1).getTime();
        if (contiguousOrOverlap) {
          last.end = new Date(Math.max(last.end.getTime(), cur.end.getTime()));
        } else {
          acc.push({ ...cur });
        }
        return acc;
      }, []);

    const childStarts = childTasks.map((t) => t.start.getTime());
    const childEnds = childTasks.map((t) => (t.end ? t.end.getTime() : t.start.getTime()));
    const summaryStart = childStarts.length > 0 ? new Date(Math.min(...childStarts)) : rangeStart;
    const summaryEnd = childEnds.length > 0 ? new Date(Math.max(...childEnds)) : rangeEnd;
    const plannedStarts = childTasks
      .map((t) => (t.plannedStartDate ? parseDateOnly(t.plannedStartDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const plannedCompletions = childTasks
      .map((t) => (t.plannedCompletionDate ? parseDateOnly(t.plannedCompletionDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const { plannedStartDate, plannedCompletionDate } = formatSummaryPlannedDates({
      plannedStartDates: plannedStarts,
      plannedCompletionDates: plannedCompletions,
    });

    const rowTitle = `${row.name}${row.serviceArea?.name ? ` (${row.serviceArea.name})` : ''}`;
    const hasChildren = childTasks.length > 0;
    svarTasks.push({
      id: typeRowId,
      text: rowTitle,
      start: summaryStart,
      end: summaryEnd,
      progress: 0,
      type: hasChildren ? 'summary' : 'summary-empty',
      parent: 0,
      open: hasChildren ? true : undefined,
      foremanArea: '—',
      position: row.position ?? row.name,
      plannedStartDate,
      plannedCompletionDate,
      occupiedSegments,
    });

    svarTasks.push(...childTasks);
  }

  return svarTasks;
}

/** @param serviceAreaFilterIds empty = every service area (the multiselect's "All"). */
function analyzeVehicleTypeCapacity(
  resources: ScheduleResource[],
  vehicleMeta: ScheduleVehicle[] | null,
  rangeStart: Date,
  rangeEnd: Date,
  serviceAreaFilterIds: number[]
): VehicleCapacityAnalysis {
  const allowedServiceAreaIds = serviceAreaFilterIds.length > 0 ? new Set(serviceAreaFilterIds) : null;
  const rows = Array.isArray(resources) ? resources : [];
  const meta = Array.isArray(vehicleMeta) ? vehicleMeta : [];
  const unitToType = new Map<string, string>();
  const availableByKey = new Map<string, number>();

  for (const v of meta) {
    const typeKey = (v.vehicleType || 'Unspecified').trim() || 'Unspecified';
    const unit = (v.unitNumber || '').trim();
    if (unit) unitToType.set(unit, typeKey);
    const sas = Array.isArray(v.serviceAreas) ? v.serviceAreas : [];
    for (const sa of sas) {
      if (allowedServiceAreaIds && !allowedServiceAreaIds.has(sa.id)) continue;
      const k = `${sa.id}|${typeKey}`;
      availableByKey.set(k, (availableByKey.get(k) || 0) + 1);
    }
  }

  const byDayRequired = new Map<string, number>();
  const maxRequired = new Map<string, number>();
  const byDayTaskIds = new Map<string, Set<number>>();
  const chartRange = { start: rangeStart, end: rangeEnd };

  for (const r of rows) {
    const sa = r.serviceArea;
    if (!sa?.id) continue;
    if (allowedServiceAreaIds && !allowedServiceAreaIds.has(sa.id)) continue;
    const tasks = Array.isArray(r.tasks) ? r.tasks : [];
    for (const task of tasks) {
      const start = parseTaskStartDate(task, chartRange);
      if (!start) continue;
      const end = parseTaskEndDate(task, start, chartRange);
      const clampedStart = new Date(Math.max(start.getTime(), rangeStart.getTime()));
      const clampedEnd = new Date(Math.min(end.getTime(), rangeEnd.getTime()));
      if (clampedEnd.getTime() < clampedStart.getTime()) continue;

      const typeCounts = new Map<string, number>();
      const vehicles = Array.isArray(task.vehicles) ? task.vehicles : [];
      for (const v of vehicles) {
        const unit = (v.unitUsed || '').trim();
        if (!unit) continue;
        const t = unitToType.get(unit) || 'Unspecified';
        typeCounts.set(t, (typeCounts.get(t) || 0) + 1);
      }
      if (typeCounts.size === 0) continue;

      for (let d = startOfDay(clampedStart); d.getTime() <= clampedEnd.getTime(); d = addDays(d, 1)) {
        const dayKey = formatDate(d, 'yyyy-MM-dd');
        typeCounts.forEach((count, typeKey) => {
          const key = `${sa.id}|${typeKey}`;
          const byDayKey = `${dayKey}|${key}`;
          const dayRequired = (byDayRequired.get(byDayKey) || 0) + count;
          byDayRequired.set(byDayKey, dayRequired);
          maxRequired.set(key, Math.max(maxRequired.get(key) || 0, dayRequired));
          const taskIds = byDayTaskIds.get(byDayKey) || new Set<number>();
          taskIds.add(task.id);
          byDayTaskIds.set(byDayKey, taskIds);
        });
      }
    }
  }

  const conflicts: EquipmentCapacityConflict[] = [];
  const overloadedKeys = new Set<string>();
  maxRequired.forEach((required, key) => {
    const available = availableByKey.get(key) || 0;
    if (required > available) overloadedKeys.add(key);
  });

  const conflictTaskIds = new Set<number>();
  byDayTaskIds.forEach((taskIds, byDayKey) => {
    const delim = byDayKey.indexOf('|');
    const key = delim >= 0 ? byDayKey.slice(delim + 1) : '';
    if (!key || !overloadedKeys.has(key)) return;
    const available = availableByKey.get(key) || 0;
    const required = byDayRequired.get(byDayKey) || 0;
    if (required <= available) return;
    taskIds.forEach((id) => conflictTaskIds.add(id));
  });

  maxRequired.forEach((required, key) => {
    const available = availableByKey.get(key) || 0;
    if (required <= available) return;
    const [saId, vehicleType] = key.split('|');
    const serviceAreaName =
      rows.find((r) => String(r.serviceArea?.id || '') === saId)?.serviceArea?.name || `Service Area ${saId}`;
    conflicts.push({
      serviceArea: serviceAreaName,
      vehicleType: vehicleType || 'Unspecified',
      available,
      required,
    });
  });
  conflicts.sort((a, b) => a.serviceArea.localeCompare(b.serviceArea) || a.vehicleType.localeCompare(b.vehicleType));
  return {
    conflicts,
    conflictTaskIds,
    overloadedRowCount: overloadedKeys.size,
  };
}

const ResourceGanttChart: React.FC<ResourceGanttChartProps> = ({
  filters,
  scheduleData,
  setScheduleData,
  loading,
  setLoading,
  groupBy = 'employee',
  fillerActivities = [],
  foremanAreas = [],
  defaultContractYear = '',
  scalePreset,
}) => {
  const openTaskInNewTab = useCallback(
    (taskId: number) => {
      const taskUrl = `/qp-tracking?taskId=${taskId}`;
      window.open(taskUrl, '_blank', 'noopener,noreferrer');
    },
    []
  );
  const [loadError, setLoadError] = useState<'forbidden' | 'error' | null>(null);
  const showFillerPanel = false; // Keep functionality wired, hide UI for now.
  const [isDraggingFiller, setIsDraggingFiller] = useState(false);
  const [isCreatingFillerTask, setIsCreatingFillerTask] = useState(false);
  const [fillerDropError, setFillerDropError] = useState<string | null>(null);

  const [ganttApi, setGanttApi] = useState<unknown>(null);
  const initGantt = useCallback((api: unknown) => setGanttApi(api), []);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const bottomHScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomHScrollInnerRef = useRef<HTMLDivElement | null>(null);
  const tasksRef = useRef<SvarTask[]>([]);
  const dragOverRowIdRef = useRef<number | null>(null);
  const scheduleDataRef = useRef<ScheduleResource[] | null>(null);
  const [vehicleMeta, setVehicleMeta] = useState<ScheduleVehicle[] | null>(null);
  const [pendingConflict, setPendingConflict] = useState<PendingScheduleConflict | null>(null);
  const [isConflictDetailsOpen, setIsConflictDetailsOpen] = useState(false);

  const focusFirstConflictRow = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const targetCell = viewport.querySelector(
      '.schedule-conflict-cell'
    ) as HTMLElement | null;
    if (!targetCell) return;
    targetCell.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    targetCell.classList.remove('schedule-conflict-cell-focus');
    window.setTimeout(() => {
      targetCell.classList.add('schedule-conflict-cell-focus');
      window.setTimeout(() => targetCell.classList.remove('schedule-conflict-cell-focus'), 1600);
    }, 120);
  }, []);

  const { dateFrom, dateTo, scalesConfig } = useMemo(
    () => computeScheduleGanttTimeline(filters.startDate, filters.endDate, scalePreset),
    [scalePreset, filters.startDate, filters.endDate]
  );

  /**
   * Wire form of the multiselect area/work-type filters (comma-separated, `undefined` when empty).
   * Strings, so they stay stable effect deps no matter how the arrays were rebuilt.
   */
  const serviceAreaParam = toScheduleListParam(filters.serviceAreas);
  const foremanAreaParam = toScheduleListParam(filters.foremanAreas);
  const workTypeParam = toScheduleListParam(filters.workTypes);

  const serviceAreaFilterIds = useMemo(
    () => parseServiceAreaFilterIds(filters.serviceAreas),
    [filters.serviceAreas]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setVehicleMeta(null);

    const resourcesPromise = getScheduleResources({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      includeUnassignedVehicleTasks: groupBy === 'vehicle',
      peopleSchedule: groupBy === 'employee',
    });

    const vehiclesPromise = getScheduleVehicles({
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
    });

    Promise.allSettled([resourcesPromise, vehiclesPromise])
      .then(([resourcesResult, vehiclesResult]) => {
        if (!cancelled) {
          const vehicleRows =
            vehiclesResult.status === 'fulfilled' && Array.isArray(vehiclesResult.value)
              ? vehiclesResult.value
              : [];
          if (resourcesResult.status === 'fulfilled') {
            setScheduleData(
              filterResourcesByTaskFilters(
                Array.isArray(resourcesResult.value) ? resourcesResult.value : [],
                vehicleRows,
                filters
              )
            );
            setLoadError(null);
          } else {
            console.error('Failed to load schedule resources:', resourcesResult.reason);
            setScheduleData(null);
            const status = (resourcesResult.reason as { response?: { status?: number } })?.response?.status;
            setLoadError(status === 403 ? 'forbidden' : 'error');
          }

          if (vehiclesResult.status === 'fulfilled') {
            setVehicleMeta(vehicleRows);
          } else {
            console.error('Failed to load schedule vehicles:', vehiclesResult.reason);
            // Do not block the chart when vehicle meta fails.
            setVehicleMeta(null);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
  }, [
    dateFrom,
    dateTo,
    serviceAreaParam,
    foremanAreaParam,
    workTypeParam,
    filters.equipmentTypes,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    filters.startDate,
    filters.endDate,
    groupBy,
    setScheduleData,
    setLoading,
  ]);

  const rangeStart = useMemo(() => parseDateOnly(dateFrom) ?? parseISO(dateFrom), [dateFrom]);
  const rangeEnd = useMemo(() => parseDateOnly(dateTo) ?? parseISO(dateTo), [dateTo]);
  const chartRangeForConflictsRef = useRef({ start: rangeStart, end: rangeEnd });
  chartRangeForConflictsRef.current = { start: rangeStart, end: rangeEnd };

  const collectConflictsForUpdatedTask = useCallback(
    (
      sourceTaskId: number,
      updatedStart: Date,
      updatedEnd: Date
    ): { resourceConflicts: ScheduleConflictDetail[]; assignedResourceCount: number } => {
      const currentData = Array.isArray(scheduleDataRef.current) ? scheduleDataRef.current : [];
      const resourceConflicts: ScheduleConflictDetail[] = [];
      let assignedResourceCount = 0;

      for (const resource of currentData) {
        const resourceTasks = Array.isArray(resource.tasks) ? resource.tasks : [];
        const hasTargetTask = resourceTasks.some((task) => task.id === sourceTaskId);
        if (!hasTargetTask) continue;
        assignedResourceCount += 1;

        for (const task of resourceTasks) {
          if (task.id === sourceTaskId) continue;
          const cr = chartRangeForConflictsRef.current;
          const taskStart = parseTaskStartDate(task, cr);
          if (!taskStart) continue;
          const taskEnd = parseTaskEndDate(task, taskStart, cr);
          if (!dateRangesOverlap(updatedStart, updatedEnd, taskStart, taskEnd)) continue;

          resourceConflicts.push({
            resourceName: `${resource.name} ${resource.lastName}`.trim() || resource.position || `Person #${resource.id}`,
            taskId: task.id,
            taskName: task.name || task.description || `Task #${task.id}`,
            startDate: formatDate(taskStart, 'yyyy-MM-dd'),
            endDate: formatDate(taskEnd, 'yyyy-MM-dd'),
          });
        }
      }

      return { resourceConflicts, assignedResourceCount };
    },
    []
  );

  const reloadScheduleData = useCallback(() => {
    return getScheduleResources({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      includeUnassignedVehicleTasks: groupBy === 'vehicle',
      peopleSchedule: groupBy === 'employee',
    }).then((data: ScheduleResource[]) =>
      getScheduleVehicles({
        serviceArea: serviceAreaParam,
        foremanArea: foremanAreaParam,
      }).then((vehicles) => {
        setVehicleMeta(Array.isArray(vehicles) ? vehicles : []);
        setScheduleData(filterResourcesByTaskFilters(data, vehicles, filters));
      })
    );
  }, [
    dateFrom,
    dateTo,
    serviceAreaParam,
    foremanAreaParam,
    workTypeParam,
    filters.equipmentTypes,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    groupBy,
    setScheduleData,
    filters.startDate,
    filters.endDate,
  ]);

  type FillerDragPayload = {
    activityId: number;
    activityName: string;
    activityNumber: string;
  };

  /**
   * A dropped filler task has to land in exactly one foreman area, so the multiselect only supplies
   * a target when a single area is selected. With several (or none) selected we fall back to the row
   * the task was dropped on, and the drag handle stays disabled (see `hasForemanAreaFilter`).
   */
  const selectedSingleForemanAreaId = useMemo(() => {
    const selected = normalizeScheduleFilterValues(filters.foremanAreas);
    if (selected.length !== 1) return null;
    const id = Number(selected[0]);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [filters.foremanAreas]);

  const resolveForemanAreaIdForDrop = useCallback(
    (rowTask: SvarTask | null): number | null => {
      if (selectedSingleForemanAreaId != null) return selectedSingleForemanAreaId;

      const foremanName = String(rowTask?.foremanArea || '').trim().toLowerCase();
      if (!foremanName || foremanName === '—') return null;

      const found = foremanAreas.find((fa) => String(fa.name || '').trim().toLowerCase() === foremanName);
      return found?.id ?? null;
    },
    [selectedSingleForemanAreaId, foremanAreas]
  );

  const hasForemanAreaFilter = selectedSingleForemanAreaId != null;

  const detectSyntheticRowIdAtPoint = useCallback((x: number, y: number): number | null => {
    const parseDataId = (el: HTMLElement | null): number | null => {
      if (!el) return null;
      const idEl = el.closest?.('[data-id]') as HTMLElement | null;
      const raw = idEl?.getAttribute?.('data-id');
      const parsed = raw ? Number(raw) : NaN;
      return Number.isFinite(parsed) ? parsed : null;
    };

    const direct = parseDataId(document.elementFromPoint(x, y) as HTMLElement | null);
    if (direct != null) return direct;

    const viewport = viewportRef.current;
    if (!viewport) return null;
    const candidates = Array.from(viewport.querySelectorAll('[data-id]')) as HTMLElement[];
    let best: { id: number; distance: number } | null = null;
    for (const node of candidates) {
      const raw = node.getAttribute('data-id');
      const parsed = raw ? Number(raw) : NaN;
      if (!Number.isFinite(parsed)) continue;
      const rect = node.getBoundingClientRect();
      if (y < rect.top || y > rect.bottom) continue;
      const centerY = (rect.top + rect.bottom) / 2;
      const horizontalPenalty = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
      const distance = Math.abs(centerY - y) + horizontalPenalty;
      if (!best || distance < best.distance) {
        best = { id: parsed, distance };
      }
    }
    return best?.id ?? null;
  }, []);

  const handleFillerDrop = useCallback(
    async (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      setFillerDropError(null);

      if (!Array.isArray(fillerActivities) || fillerActivities.length === 0) return;
      if (!hasForemanAreaFilter) {
        setFillerDropError('Please apply a Foreman Area filter before dragging a filler activity.');
        return;
      }

      const raw = ev.dataTransfer.getData('application/x-filler-activity') || ev.dataTransfer.getData('text/plain');
      if (!raw) return;

      let payload: FillerDragPayload | null = null;
      try {
        payload = JSON.parse(raw) as FillerDragPayload;
      } catch {
        payload = null;
      }
      if (!payload || !payload.activityId) return;
      if (!defaultContractYear) {
        setFillerDropError('No contract year available. Refresh the page and try again.');
        return;
      }

      const apiState = (ganttApi as { getState?: () => { focusCell?: { row?: number | string } } } | null)?.getState?.();
      const focusRowId = apiState?.focusCell?.row;
      let syntheticId = dragOverRowIdRef.current ?? (typeof focusRowId === 'string' ? Number(focusRowId) : Number(focusRowId));

      if (!Number.isFinite(syntheticId)) {
        syntheticId = detectSyntheticRowIdAtPoint(ev.clientX, ev.clientY) ?? NaN;
      }

      if (!Number.isFinite(syntheticId)) {
        setFillerDropError('Drop on a schedule row to create a filler task.');
        return;
      }
      dragOverRowIdRef.current = null;

      const droppedTask = tasksRef.current.find((t) => t.id === syntheticId) ?? null;
      if (!droppedTask) return;

      const rowTask =
        droppedTask.type === 'task'
          ? tasksRef.current.find((t) => t.id === droppedTask.parent) ?? droppedTask
          : droppedTask;

      const foremanAreaId = resolveForemanAreaIdForDrop(rowTask);
      if (!foremanAreaId) {
        setFillerDropError('Select a Foreman Area filter (or drop on a row with a known foreman area).');
        return;
      }

      let assignedVehicleUnitNumbers: string[] = [];
      if (groupBy === 'vehicle') {
        const candidateUnit =
          rowTask.parent && rowTask.parent !== 0
            ? rowTask
            : tasksRef.current.find((t) => t.id === rowTask.parent) ?? null;
        const isUnitSummary =
          candidateUnit != null &&
          (candidateUnit.type === 'summary' || candidateUnit.type === 'summary-empty') &&
          candidateUnit.parent != null &&
          candidateUnit.parent !== 0;
        const unitText = isUnitSummary ? String(candidateUnit?.text || '').trim() : '';
        if (unitText && unitText.toLowerCase() !== 'unassigned') {
          assignedVehicleUnitNumbers = [unitText];
        }
      }

      const today = startOfDay(new Date());
      const tomorrow = addDays(today, 1);

      setIsCreatingFillerTask(true);
      try {
        await taskService.createTask({
          activityId: payload.activityId,
          activityNumber: payload.activityNumber,
          name: `${payload.activityName} ${payload.activityNumber}`.trim(),
          priority: 'Low',
          identifiedDate: today,
          completionDate: null,
          contractYear: defaultContractYear,
          status: 'PLANNED',
          taskType: 'Quantified',
          foremanArea: foremanAreaId,
          plannedCompletionDate: tomorrow,
          quantifiedTasks: [],
          taskLocations: [],
          taskCostings: [],
          assignedVehicleUnitNumbers,
        } as any);
        await reloadScheduleData();
      } catch (error) {
        console.error('Failed to create filler task:', error);
        setFillerDropError('Could not create filler task. Please try again.');
      } finally {
        setIsCreatingFillerTask(false);
      }
    },
    [
      defaultContractYear,
      fillerActivities,
      foremanAreas,
      ganttApi,
      groupBy,
      hasForemanAreaFilter,
      reloadScheduleData,
      detectSyntheticRowIdAtPoint,
      resolveForemanAreaIdForDrop,
    ]
  );

  const vehicleCapacityAnalysis = useMemo(() => {
    if (groupBy !== 'vehicle') return null;
    return analyzeVehicleTypeCapacity(
      Array.isArray(scheduleData) ? scheduleData : [],
      vehicleMeta,
      rangeStart,
      rangeEnd,
      serviceAreaFilterIds
    );
  }, [groupBy, scheduleData, vehicleMeta, rangeStart, rangeEnd, serviceAreaFilterIds]);

  const { tasks, scales, links, conflictTaskCount, overloadedRowCount } = useMemo(() => {
    const baseResources = Array.isArray(scheduleData) ? scheduleData : [];

    const chartResources =
      groupBy === 'vehicle'
        ? buildVehicleResources(baseResources, vehicleMeta, serviceAreaFilterIds)
        : baseResources;
    const conflictSource =
      groupBy === 'vehicle'
        ? {
            conflictTaskIds: vehicleCapacityAnalysis?.conflictTaskIds || new Set<number>(),
            overloadedRowCount: vehicleCapacityAnalysis?.overloadedRowCount || 0,
          }
        : collectConflictTaskIds(chartResources, { start: rangeStart, end: rangeEnd });
    const { conflictTaskIds, overloadedRowCount: overloaded } = conflictSource;

    const svarTasks =
      groupBy === 'vehicle'
        ? vehicleUnitsToSvarTasks(chartResources as ScheduleResource[], rangeStart, rangeEnd)
        : resourcesToSvarTasks(chartResources as ScheduleResource[], rangeStart, rangeEnd);
    const scalesList = scalesConfig;
    const safeTasks = (Array.isArray(svarTasks) ? svarTasks : []).filter(
      (t): t is SvarTask =>
        t != null &&
        typeof t.id === 'number' &&
        t.start instanceof Date &&
        t.end instanceof Date
    );
    const ids = new Set(safeTasks.map((t) => t.id));
    const treeSafeTasks = safeTasks
      .filter((t) => t.parent == null || t.parent === 0 || ids.has(t.parent))
      .map((t) => {
        if (!t.sourceTaskId || !conflictTaskIds.has(t.sourceTaskId)) return t;
        const flaggedText = t.text.includes('[CONFLICT]') ? t.text : `${t.text} [CONFLICT]`;
        return { ...t, text: flaggedText };
      });
    const dependencyLinks = buildSvarDependencyLinks(treeSafeTasks);
    return {
      tasks: treeSafeTasks,
      scales: scalesList,
      links: dependencyLinks,
      conflictTaskCount: conflictTaskIds.size,
      overloadedRowCount: overloaded,
    };
  }, [
    scheduleData,
    rangeStart,
    rangeEnd,
    scalesConfig,
    groupBy,
    vehicleMeta,
    serviceAreaFilterIds,
    vehicleCapacityAnalysis,
  ]);

  const equipmentCapacityConflicts = useMemo(() => {
    if (groupBy !== 'vehicle') return [] as EquipmentCapacityConflict[];
    return vehicleCapacityAnalysis?.conflicts || [];
  }, [groupBy, vehicleCapacityAnalysis]);

  const resourceLabel = groupBy === 'vehicle' ? 'Equipment Type' : 'People';
  const positionHeader = groupBy === 'vehicle' ? 'Service Area / Capacity' : 'Position';

  const columns = useMemo(
    () => [
      { id: 'text', header: resourceLabel, width: 240 },
      { id: 'foremanArea', header: 'Foreman Area', width: 190 },
      { id: 'position', header: positionHeader, width: 180 },
      { id: 'actions', header: 'Actions', width: 120 },
    ],
    [resourceLabel, positionHeader]
  );

  const taskTypes = useMemo(
    () => [
      { id: 'task', label: 'Task' },
      { id: 'summary', label: resourceLabel },
      { id: 'summary-empty', label: `${resourceLabel} (no tasks)` },
      { id: 'planned', label: 'Planned' },
      { id: 'active', label: 'Active' },
      { id: 'overdue', label: 'Overdue' },
      { id: 'ongoing', label: 'Year-round / as needed' },
    ],
    [resourceLabel]
  );

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const safeLinks = Array.isArray(links) ? links : [];
  const safeScales = Array.isArray(scales) ? scales : [];

  useScheduleGanttChartScroll(viewportRef, bottomHScrollRef, bottomHScrollInnerRef, [
    safeTasks.length,
    loading,
    rangeStart,
    rangeEnd,
    scalePreset,
    groupBy,
  ]);

  const existingConflictNotice = useMemo<ExistingScheduleConflictNotice | null>(() => {
    if (conflictTaskCount <= 0) return null;
    return {
      conflictTaskCount,
      overloadedRowCount,
    };
  }, [conflictTaskCount, overloadedRowCount]);
  const showConflictWarning = groupBy === 'vehicle' && existingConflictNotice != null;

  useEffect(() => {
    if (!existingConflictNotice && isConflictDetailsOpen) {
      setIsConflictDetailsOpen(false);
    }
  }, [existingConflictNotice, isConflictDetailsOpen]);

  tasksRef.current = safeTasks;
  scheduleDataRef.current = scheduleData;

  const UPDATE_TASK_TAG = 'schedule-persist-dates';
  useEffect(() => {
    const api = ganttApi as {
      on?: (event: string, handler: (ev: { id: number | string; task?: { start?: Date | string; end?: Date | string }; inProgress?: boolean }) => void, options?: { tag?: string }) => void;
      detach?: (tag: string) => void;
    } | null;
    if (!api?.on) return;

    const handleUpdateTask = (ev: { id: number | string; task?: { start?: Date | string; end?: Date | string }; inProgress?: boolean }) => {
      const resolved = resolveDragEndRange(ev, ganttApi as IApi | null | undefined);
      if (!resolved) return;
      const { startDate, endDate } = resolved;
      const syntheticId = typeof ev.id === 'string' ? parseInt(ev.id, 10) : ev.id;
      if (Number.isNaN(syntheticId)) return;
      const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
      if (!svarTask?.sourceTaskId) return;

      const identifiedDateStr = formatDate(startDate, 'yyyy-MM-dd');
      const plannedCompletionDateStr = formatDate(endDate, 'yyyy-MM-dd');

      const { resourceConflicts, assignedResourceCount } = collectConflictsForUpdatedTask(
        svarTask.sourceTaskId,
        startDate,
        endDate
      );
      const hasTaskToResourceConflict = assignedResourceCount > 1;
      const hasResourceUnavailableConflict = resourceConflicts.length > 0;
      if (hasTaskToResourceConflict || hasResourceUnavailableConflict) {
        setPendingConflict({
          sourceTaskId: svarTask.sourceTaskId,
          identifiedDateStr,
          plannedCompletionDateStr,
          startDate,
          endDate,
          taskLabel: svarTask.text || `Task #${svarTask.sourceTaskId}`,
          resourceConflicts,
          assignedResourceCount,
        });
        reloadScheduleData().catch((err: unknown) => {
          console.error('Failed to reload schedule after conflict warning:', err);
        });
        return;
      }

      const currentData = scheduleDataRef.current;
      const optimisticData = updateTaskDatesInScheduleData(currentData, svarTask.sourceTaskId, identifiedDateStr, plannedCompletionDateStr);
      if (optimisticData) setScheduleData(optimisticData);

      taskService
        .updateTask(svarTask.sourceTaskId, { identifiedDate: startDate, plannedCompletionDate: endDate })
        .then(() => reloadScheduleData())
        .catch((err: unknown) => {
          console.error('Failed to persist task dates from Gantt:', err);
          reloadScheduleData().catch((reloadErr: unknown) => {
            console.error('Failed to reload schedule after update failure:', reloadErr);
          });
        });
    };

    api.on('update-task', handleUpdateTask, { tag: UPDATE_TASK_TAG });
    return () => {
      if (api?.detach) api.detach(UPDATE_TASK_TAG);
    };
  }, [ganttApi, collectConflictsForUpdatedTask, reloadScheduleData, setScheduleData]);

  const OPEN_TASK_TAG = 'schedule-open-in-task-management';
  useEffect(() => {
    const api = ganttApi as {
      on?: (event: string, handler: (ev: SvarTaskClickEvent) => void, options?: { tag?: string }) => void;
      detach?: (tag: string) => void;
    } | null;
    if (!api?.on) return;

    const openTaskInTaskManagement = (ev: SvarTaskClickEvent) => {
      const syntheticId = resolveSyntheticTaskIdFromEvent(ev);
      if (syntheticId == null) return;
      const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
      if (!svarTask?.sourceTaskId) return;
      openTaskInNewTab(svarTask.sourceTaskId);
    };

    const clickEvents = ['click-task', 'task-click', 'open-task'];
    clickEvents.forEach((eventName) => {
      api.on?.(eventName, openTaskInTaskManagement, { tag: OPEN_TASK_TAG });
    });

    return () => {
      if (api?.detach) api.detach(OPEN_TASK_TAG);
    };
  }, [ganttApi, openTaskInNewTab]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const parseSyntheticIds = (candidate: string | null | undefined): number[] => {
      if (!candidate) return [];
      const matches = candidate.match(/-?\d+/g) || [];
      return matches
        .map((token) => Number.parseInt(token, 10))
        .filter((num) => Number.isFinite(num));
    };

    const resolveSyntheticIdFromElement = (startEl: HTMLElement): number | null => {
      const validIds = new Set(tasksRef.current.map((task) => task.id));
      let node: HTMLElement | null = startEl;
      while (node && node !== viewport) {
        const candidates: string[] = [];
        for (const attr of Array.from(node.attributes)) {
          candidates.push(attr.value);
        }
        if (node.dataset) {
          Object.values(node.dataset).forEach((value) => {
            if (value) candidates.push(value);
          });
        }
        if (node.className) candidates.push(String(node.className));
        if (node.id) candidates.push(node.id);

        for (const candidate of candidates) {
          const ids = parseSyntheticIds(candidate);
          const hit = ids.find((id) => validIds.has(id));
          if (hit != null) return hit;
        }
        node = node.parentElement;
      }

      const rowEl = startEl.closest('.wx-row') as HTMLElement | null;
      if (rowEl) {
        const rows = Array.from(viewport.querySelectorAll('.wx-grid .wx-row')) as HTMLElement[];
        const rowIndex = rows.indexOf(rowEl);
        if (rowIndex >= 0 && rowIndex < tasksRef.current.length) {
          return tasksRef.current[rowIndex]?.id ?? null;
        }
      }

      return null;
    };

    const handleViewportClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const actionCell = target.closest('.wx-cell') as HTMLElement | null;
      const isActionCell = (actionCell?.textContent || '').toLowerCase().includes('view task');
      const clickedChartElement = target.closest('.wx-bar, .wx-task');
      if (!isActionCell && !clickedChartElement) return;
      const syntheticId = resolveSyntheticIdFromElement(target);
      if (syntheticId == null) return;
      const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
      if (!svarTask?.sourceTaskId) return;
      openTaskInNewTab(svarTask.sourceTaskId);
    };

    viewport.addEventListener('click', handleViewportClick);
    return () => {
      viewport.removeEventListener('click', handleViewportClick);
    };
  }, [openTaskInNewTab]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const resolveSyntheticIdFromElement = (startEl: HTMLElement): number | null => {
      const validIds = new Set(tasksRef.current.map((task) => task.id));
      let node: HTMLElement | null = startEl;
      while (node && node !== viewport) {
        const values = Array.from(node.attributes).map((attr) => attr.value);
        if (node.className) values.push(String(node.className));
        if (node.id) values.push(node.id);
        if (node.dataset) {
          Object.values(node.dataset).forEach((value) => {
            if (value) values.push(value);
          });
        }
        for (const value of values) {
          const matches = value.match(/-?\d+/g) || [];
          for (const token of matches) {
            const id = Number.parseInt(token, 10);
            if (Number.isFinite(id) && validIds.has(id)) return id;
          }
        }
        node = node.parentElement;
      }
      return null;
    };

    const decorateActionCells = () => {
      const actionCells = Array.from(viewport.querySelectorAll('.wx-cell')) as HTMLElement[];
      actionCells.forEach((cell) => {
        if (!(cell.textContent || '').toLowerCase().includes('view task')) return;
        if (cell.querySelector('a[href*="/qp-tracking?taskId="]')) return;
        const syntheticId = resolveSyntheticIdFromElement(cell);
        if (syntheticId == null) return;
        const svarTask = tasksRef.current.find((t) => t.id === syntheticId);
        const sourceTaskId = svarTask?.sourceTaskId;
        if (!sourceTaskId) return;

        const taskUrl = `/qp-tracking?taskId=${sourceTaskId}`;
        cell.textContent = '';
        cell.style.whiteSpace = 'nowrap';

        const dots = document.createElement('span');
        dots.textContent = '⋮';
        dots.style.marginRight = '6px';
        dots.style.color = '#6c757d';
        dots.style.fontWeight = '600';

        const link = document.createElement('a');
        link.href = taskUrl;
        link.textContent = 'View task';
        link.style.textDecoration = 'underline';
        link.style.color = '#0d6efd';
        link.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openTaskInNewTab(sourceTaskId);
        });

        cell.appendChild(dots);
        cell.appendChild(link);
      });
    };

    decorateActionCells();
    const timerId = window.setTimeout(decorateActionCells, 150);
    const observer = new MutationObserver(decorateActionCells);
    observer.observe(viewport, { childList: true, subtree: true });
    return () => {
      window.clearTimeout(timerId);
      observer.disconnect();
    };
  }, [safeTasks, openTaskInNewTab]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const applyPinnedScale = () => {
      const scale = viewport.querySelector('div.wx-scale') as HTMLElement | null;
      if (!scale) return;

      const y = viewport.scrollTop;
      scale.style.transform = `translateY(${y}px)`;
      scale.style.zIndex = '20';
      scale.style.background = '#fff';
      scale.style.boxShadow = '0 1px 0 0 rgba(0, 0, 0, 0.1)';
      scale.style.willChange = 'transform';

      // Pin exactly one left-header container to avoid duplicated stacked headers.
      const leftHeaderContainer =
        (viewport.querySelector('.wx-header-wrapper') as HTMLElement | null) ||
        (viewport.querySelector('.wx-grid-header') as HTMLElement | null) ||
        (viewport.querySelector('.wx-grid thead') as HTMLElement | null);
      if (leftHeaderContainer) {
        leftHeaderContainer.style.transform = `translateY(${y}px)`;
        leftHeaderContainer.style.position = 'relative';
        leftHeaderContainer.style.zIndex = '60';
        leftHeaderContainer.style.background = '#d6e4ff';
        leftHeaderContainer.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.06)';
        leftHeaderContainer.style.overflow = 'visible';
        leftHeaderContainer.style.willChange = 'transform';
      }

      // Ensure nested header rows/cells don't get separately transformed.
      const nestedHeaderRows = viewport.querySelectorAll<HTMLElement>('.wx-header, .wx-h-row');
      nestedHeaderRows.forEach((el) => {
        el.style.transform = '';
        el.style.willChange = '';
      });

      const today = startOfDay(new Date());
      const scaleWidth = Number((scale.style.width || '0px').replace('px', '')) || scale.offsetWidth;
      const rows = scale.querySelectorAll('.wx-row');
      const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
      const cells = lastRow ? (Array.from(lastRow.querySelectorAll('.wx-cell')) as HTMLElement[]) : [];
      const scaleRect = scale.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const scaleLeftInViewport = scaleRect.left - viewportRect.left;
      const todayXInScale = computeTodayMarkerOffsetInScalePx({
        scaleWidth,
        rangeStart,
        rangeEnd,
        scalePreset,
        cells,
        today,
      });

      const lineLeft = scaleLeftInViewport + todayXInScale;
      const lineTop = viewport.scrollTop + scale.offsetHeight;
      const lineHeight = Math.max(0, viewport.scrollHeight - lineTop);

      let line = viewport.querySelector('.schedule-today-fallback-line') as HTMLElement | null;
      if (!line) {
        line = document.createElement('div');
        line.className = 'schedule-today-fallback-line';
        line.setAttribute('role', 'presentation');
        viewport.appendChild(line);
      }
      const todayTip = `Today — ${formatDate(startOfDay(new Date()), 'MMMM d, yyyy')}`;
      line.setAttribute('title', todayTip);
      line.setAttribute('aria-label', todayTip);
      /* 12px hit area centered on previous 2px line (center was lineLeft + 1px) */
      line.style.left = `${lineLeft - 5}px`;
      line.style.top = `${lineTop}px`;
      line.style.height = `${lineHeight}px`;

      viewport.querySelectorAll('.schedule-today-marker').forEach((node) => {
        const el = node as HTMLElement;
        el.setAttribute('title', todayTip);
        el.setAttribute('aria-label', todayTip);
      });
    };

    const run = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(applyPinnedScale);
      });
    };
    run();
    const onScroll = () => applyPinnedScale();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    const hScrollEl = getScheduleGanttHorizontalScrollTarget(viewport);
    if (hScrollEl && hScrollEl !== viewport) {
      hScrollEl.addEventListener('scroll', onScroll, { passive: true });
    }
    const ro = new ResizeObserver(run);
    const scaleEl = viewport.querySelector('div.wx-scale');
    if (scaleEl) ro.observe(scaleEl);
    const timeoutId = window.setTimeout(run, 200);
    return () => {
      viewport.removeEventListener('scroll', onScroll);
      if (hScrollEl && hScrollEl !== viewport) {
        hScrollEl.removeEventListener('scroll', onScroll);
      }
      ro.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [safeTasks.length, loading, rangeStart, rangeEnd, scalePreset]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const gridCells = Array.from(
      viewport.querySelectorAll('.wx-gantt .wx-grid .wx-cell')
    ) as HTMLElement[];
    for (const cell of gridCells) {
      if ((cell.textContent || '').includes('[CONFLICT]')) {
        cell.classList.add('schedule-conflict-cell');
      } else {
        cell.classList.remove('schedule-conflict-cell', 'schedule-conflict-cell-focus');
      }
    }
  }, [safeTasks]);

  // Vehicle summary row: keep one parent row and render occupied segments with visible gaps.
  useEffect(() => {
    if (groupBy !== 'vehicle') return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    let rafId: number | null = null;
    let applying = false;

    const applySegments = () => {
      if (applying) return;
      applying = true;
      const summaryTasks = safeTasks.filter((t) => t.parent === 0 && t.type === 'summary');
      const summaryBars = Array.from(viewport.querySelectorAll('.wx-gantt .wx-bar.wx-summary')) as HTMLElement[];

      // Reset all summary bars first to avoid stale visuals.
      summaryBars.forEach((bar) => {
        bar.classList.remove('schedule-summary-segmented');
        bar.querySelectorAll('.schedule-summary-segment').forEach((node) => node.remove());
        bar.style.backgroundColor = '';
        bar.style.borderColor = '';
        bar.style.position = '';
        bar.style.overflow = '';
        bar.style.color = '';
        bar.style.textShadow = '';
      });

      // Bars and summary tasks are rendered in the same order.
      const pairCount = Math.min(summaryTasks.length, summaryBars.length);
      for (let i = 0; i < pairCount; i += 1) {
        const task = summaryTasks[i];
        const bar = summaryBars[i];
        const segmentList = Array.isArray(task.occupiedSegments) ? task.occupiedSegments : [];
        if (segmentList.length <= 1) continue;

        bar.classList.add('schedule-summary-segmented');
        bar.style.backgroundColor = '#ffffff';
        bar.style.borderColor = 'rgba(73, 80, 87, 0.45)';
        bar.style.position = 'relative';
        bar.style.overflow = 'hidden';
        bar.style.color = '#111111';
        bar.style.textShadow = 'none';

        // Use the same continuous timeline basis as rendered bars (exclusive end math)
        // so segment edges align exactly with child task bars.
        const taskStartMs = task.start.getTime();
        const taskEndMs = (task.end ? task.end : task.start).getTime();
        const totalMs = Math.max(1, taskEndMs - taskStartMs);

        segmentList.forEach((seg) => {
          const segStartMs = seg.start.getTime();
          const segEndMs = seg.end.getTime();
          const leftPct = ((segStartMs - taskStartMs) / totalMs) * 100;
          const widthPct = ((segEndMs - segStartMs) / totalMs) * 100;
          const segEl = document.createElement('span');
          segEl.className = 'schedule-summary-segment';
          segEl.style.left = `${Math.max(0, leftPct)}%`;
          segEl.style.width = `${Math.max(0.5, widthPct)}%`;
          bar.appendChild(segEl);
        });
      }
      applying = false;
    };

    const scheduleApply = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        requestAnimationFrame(applySegments);
      });
    };

    scheduleApply();
    const observer = new MutationObserver(scheduleApply);
    observer.observe(viewport, { subtree: true, childList: true, attributes: true });
    const onResize = () => scheduleApply();
    window.addEventListener('resize', onResize);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [groupBy, safeTasks]);

  const plannedDatesMap = useMemo(() => {
    const m: PlannedDatesMap = new Map();
    for (const t of safeTasks) {
      const hasPlannedFields = t.plannedStartDate !== undefined || t.plannedCompletionDate !== undefined;
      const hasAccomplishment = t.identifiedAccomplishment != null;
      if (!hasPlannedFields && !t.yearRoundSchedule && !hasAccomplishment) continue;
      m.set(t.id, {
        plannedStartDate: t.plannedStartDate ?? null,
        plannedCompletionDate: t.plannedCompletionDate ?? null,
        identifiedAccomplishment:
          t.identifiedAccomplishment != null && Number.isFinite(Number(t.identifiedAccomplishment))
            ? Number(t.identifiedAccomplishment)
            : null,
        dependencies: t.dependencies ?? null,
        yearRoundSchedule: t.yearRoundSchedule === true,
      });
    }
    return m;
  }, [safeTasks]);

  const tooltipContent = useCallback(
    (props: {
      data?: { id?: number | string; text?: string; start?: Date | string; end?: Date | string; progress?: number };
      api?: unknown;
    }) => (
      <ScheduleTaskTooltipContent {...props} plannedDatesMap={plannedDatesMap} />
    ),
    [plannedDatesMap]
  );

  const todayMarker = useMemo(() => ([{
    start: startOfDay(new Date()),
    text: 'Today',
    css: 'schedule-today-marker',
  }]), []);

  const handlePrintSchedulePdf = useCallback(() => {
    try {
      const allTasks = Array.isArray(safeTasks) ? safeTasks : [];
      const summaryById = new Map<number, string>();
      for (const t of allTasks) {
        if (t.parent === 0 && (t.type === 'summary' || t.type === 'summary-empty')) {
          summaryById.set(t.id, t.text);
        }
      }

      // Only direct task rows under a resource/equipment summary (matches on-screen hierarchy; excludes nested duplicates).
      const childTasks = allTasks
        .filter((t) => t.sourceTaskId != null && t.parent != null && summaryById.has(t.parent))
        .slice()
        .sort((a, b) => a.start.getTime() - b.start.getTime());

      const sourceTaskIdToLabel = new Map<number, string>();
      allTasks.forEach((t) => {
        if (t.sourceTaskId != null) {
          sourceTaskIdToLabel.set(t.sourceTaskId, stripScheduleIconPrefix(t.text));
        }
      });

      const printableIds = new Set(childTasks.map((t) => t.id));
      const sourceTaskIdToNodeId = new Map<number, number>();
      childTasks.forEach((t) => {
        if (t.sourceTaskId != null) sourceTaskIdToNodeId.set(t.sourceTaskId, t.id);
      });
      const printLinks: Array<{ source: number; target: number }> = [];
      childTasks.forEach((t) => {
        const completionId = t.dependencies?.completionTaskId;
        if (completionId == null || t.sourceTaskId == null) return;
        const srcNode = sourceTaskIdToNodeId.get(Number(completionId));
        if (srcNode == null || srcNode === t.id || !printableIds.has(srcNode)) return;
        printLinks.push({ source: srcNode, target: t.id });
      });

      const rangeStartMs = startOfDay(rangeStart).getTime();
      const rangeEndMs = startOfDay(rangeEnd).getTime();
      const totalMs = Math.max(24 * 60 * 60 * 1000, rangeEndMs - rangeStartMs);

      const barColor = (type: string): string => {
        if (type === 'overdue') return '#333333';
        if (type === 'active') return '#58a55c';
        if (type === 'ongoing') return '#6f42c1';
        return '#86b8e7';
      };

      const toDate = (val?: string | null): Date | null => {
        if (!val) return null;
        return parseDateOnly(val);
      };

      const fmtCellDate = (d: Date | null): string => (d ? formatDate(d, 'EEE yy-MM-dd') : '');

      const monthTicks: Array<{ left: number; label: string }> = [];
      {
        const startDate = new Date(rangeStartMs);
        const tick = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        if (tick.getTime() < rangeStartMs) tick.setMonth(tick.getMonth() + 1, 1);
        while (tick.getTime() <= rangeEndMs) {
          const left = ((tick.getTime() - rangeStartMs) / totalMs) * 100;
          monthTicks.push({ left, label: formatDate(tick, 'MMM yyyy') });
          tick.setMonth(tick.getMonth() + 1, 1);
        }
      }

      const rowsHtml = childTasks
        .map((t, idx) => {
          const sectionLabel = t.parent != null ? summaryById.get(t.parent) ?? '' : '';
          const startDate = toDate(t.plannedStartDate) ?? startOfDay(t.start);
          const endDate = toDate(t.plannedCompletionDate) ?? startOfDay(t.end ?? t.start);

          const startMs = Math.max(startDate.getTime(), rangeStartMs);
          const endMsRaw = Math.min(endDate.getTime(), rangeEndMs);
          const endMs = Math.max(startMs + 24 * 60 * 60 * 1000, endMsRaw);

          const leftPct = ((startMs - rangeStartMs) / totalMs) * 100;
          const widthPct = Math.max(0.8, ((endMs - startMs) / totalMs) * 100);

          const notesText = formatDependencyNotes(t.dependencies, sourceTaskIdToLabel);
          const positionHint = t.position && String(t.position).trim() ? String(t.position).trim() : '';
          const notesCombined = [notesText, positionHint].filter(Boolean).join(positionHint && notesText ? ' | ' : '');

          return `
            <tr>
              <td class="num">${idx + 1}</td>
              <td>${escapeHtml(t.foremanArea ?? '—')}</td>
              <td class="task-name">${escapeHtml(sectionLabel || t.project || '—')}<div class="sub">${escapeHtml(stripScheduleIconPrefix(t.text))}</div></td>
              <td class="date">${escapeHtml(fmtCellDate(startDate))}</td>
              <td class="date">${escapeHtml(fmtCellDate(endDate))}</td>
              <td class="qty">—</td>
              <td class="uom">${escapeHtml(groupBy === 'vehicle' ? 'Vehicle' : 'Hours')}</td>
              <td class="notes">${escapeHtml(notesCombined)}</td>
              <td class="timeline-cell" data-print-node-id="${t.id}">
                <div class="timeline-track">
                  <div class="timeline-bar-blue print-bar-end print-bar-start" style="left:${leftPct}%;width:${widthPct}%"></div>
                  <div class="timeline-bar-overlay print-bar-end print-bar-start" style="left:${leftPct}%;width:${widthPct}%;background:${barColor(t.type)}"></div>
                  <div class="timeline-label" style="left:${leftPct}%">${escapeHtml(fmtCellDate(startDate))} - ${escapeHtml(fmtCellDate(endDate))}</div>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');

      const ticksHtml = monthTicks
        .map((tick) => `<div class="tick" style="left:${tick.left}%"><span>${escapeHtml(tick.label)}</span></div>`)
        .join('');

      const scheduleTitle = 'Dawson Road Maintenance';
      const subtitle = groupBy === 'vehicle' ? 'Equipment Plan Schedule' : 'People Plan Schedule';
      /** Multiselect values print as a comma-separated list; empty selections are omitted. */
      const filterList = (label: string, values: Array<string | number> | undefined | null) => {
        const normalized = normalizeScheduleFilterValues(values);
        return normalized.length > 0 ? `${label}: ${normalized.join(', ')}` : null;
      };
      const filtersSummary = [
        filterList('Service Area', filters.serviceAreas),
        filterList('Foreman Area', filters.foremanAreas),
        filterList('Equipment Type', filters.equipmentTypes),
        filterList('Project', filters.projectIds),
        filterList('Status', filters.statuses),
        filters.startDate ? `Start Date: ${filters.startDate}` : null,
        filters.endDate ? `End Date: ${filters.endDate}` : null,
        filterList('Work Type', filters.workTypes),
        filters.taskSearch ? `Search: ${filters.taskSearch}` : null,
      ]
        .filter(Boolean)
        .join(' | ');

      const arrowScript = buildPrintDependencyArrowInitScript(printLinks);

      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(subtitle)}</title>
            <style>
              @page { size: A4 landscape; margin: 8mm; }
              * { box-sizing: border-box; }
              body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #111; position: relative; }
              .title { text-align: center; font-size: 12px; font-weight: 700; margin-top: 2px; }
              .subtitle { text-align: center; font-size: 10px; margin-top: 2px; margin-bottom: 6px; }
              .meta { font-size: 9px; margin-bottom: 4px; color: #333; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8px; }
              th, td { border: 1px solid #a9a9a9; padding: 2px 3px; vertical-align: middle; }
              th { background: #efefef; font-weight: 700; }
              .num { width: 2%; text-align: center; }
              .fa { width: 8%; }
              .task { width: 28%; }
              .date { width: 6%; white-space: nowrap; }
              .qty { width: 4%; text-align: right; }
              .uom { width: 4%; text-align: center; }
              .notes { width: 14%; }
              .timeline { width: 28%; padding: 0; }
              .task-name { font-weight: 700; }
              .task-name .sub { font-weight: 400; margin-top: 1px; }
              .timeline-head { position: relative; height: 24px; background: #f7f7f7; overflow: hidden; }
              .timeline-head .tick { position: absolute; top: 0; bottom: 0; border-left: 1px solid #d0d0d0; width: 0; }
              .timeline-head .tick span { position: absolute; top: 1px; left: 2px; font-size: 7px; color: #555; white-space: nowrap; }
              .timeline-cell { padding: 0; background: #fafafa; position: relative; }
              .timeline-track { position: relative; height: 14px; }
              .timeline-bar-blue { position: absolute; top: 5px; height: 4px; border-radius: 1px; min-width: 2px; background: #86b8e7; }
              .timeline-bar-overlay { position: absolute; top: 5px; height: 4px; border-radius: 1px; min-width: 2px; opacity: .45; }
              .timeline-label { position: absolute; top: -8px; font-size: 6.5px; color: #2b2b2b; white-space: nowrap; }
              .legend { margin-top: 4px; font-size: 8px; }
              .legend span { display: inline-block; margin-right: 8px; }
              .swatch { display: inline-block; width: 10px; height: 4px; margin-right: 3px; vertical-align: middle; }
            </style>
          </head>
          <body>
            <svg id="schedule-print-arrows" xmlns="http://www.w3.org/2000/svg" style="position:absolute;left:0;top:0;pointer-events:none;z-index:100;overflow:visible"></svg>
            <div class="title">${escapeHtml(scheduleTitle)}</div>
            <div class="subtitle">${escapeHtml(subtitle)}</div>
            <div class="meta">${escapeHtml(filtersSummary || 'All filters')} | ${escapeHtml(`${dateFrom} to ${dateTo}`)}</div>
            <table>
              <thead>
                <tr>
                  <th class="num">#</th>
                  <th class="fa">Foreman Area</th>
                  <th class="task">Task Name</th>
                  <th class="date">Start</th>
                  <th class="date">Finish</th>
                  <th class="qty">QTY</th>
                  <th class="uom">Unit</th>
                  <th class="notes">Notes</th>
                  <th class="timeline">
                    <div class="timeline-head">${ticksHtml}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml || `<tr><td colspan="9">No tasks match current filters.</td></tr>`}
              </tbody>
            </table>
            <div class="legend">
              <span><i class="swatch" style="background:#86b8e7"></i>Planned</span>
              <span><i class="swatch" style="background:#58a55c"></i>Active</span>
              <span><i class="swatch" style="background:#333333"></i>Overdue</span>
            </div>
            <script>${arrowScript}</script>
          </body>
        </html>
      `;

      const win = window.open('', '_blank', 'width=1400,height=900');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
    } catch (err) {
      console.error('Failed to print schedule:', err);
      alert('Failed to print schedule. Please try again.');
    }
  }, [safeTasks, rangeStart, rangeEnd, groupBy, filters, dateFrom, dateTo]);

  const handleKeepConflictDates = () => {
    if (!pendingConflict) return;
    const conflict = pendingConflict;
    setPendingConflict(null);

    const optimisticData = updateTaskDatesInScheduleData(
      scheduleDataRef.current,
      conflict.sourceTaskId,
      conflict.identifiedDateStr,
      conflict.plannedCompletionDateStr
    );
    if (optimisticData) setScheduleData(optimisticData);

    taskService
      .updateTask(conflict.sourceTaskId, {
        identifiedDate: conflict.startDate,
        plannedCompletionDate: conflict.endDate,
      })
      .then(() => reloadScheduleData())
      .catch((err: unknown) => {
        console.error('Failed to persist conflicting task dates:', err);
        reloadScheduleData().catch((reloadErr: unknown) => {
          console.error('Failed to reload after conflicting update failure:', reloadErr);
        });
      });
  };

  const handleAdjustDatesAfterConflict = () => {
    setPendingConflict(null);
    reloadScheduleData().catch((err: unknown) => {
      console.error('Failed to reload schedule while cancelling conflicted change:', err);
    });
  };

  // Expose print handler so SchedulePage can render a button outside this component.
  useEffect(() => {
    (window as any).__printSchedulePdf = handlePrintSchedulePdf;
    return () => {
      if ((window as any).__printSchedulePdf === handlePrintSchedulePdf) {
        delete (window as any).__printSchedulePdf;
      }
    };
  }, [handlePrintSchedulePdf]);

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-grow tw-text-content-muted">
        Loading schedule…
      </div>
    );
  }

  if (loadError === 'forbidden') {
    return (
      <div className="tw-p-4 tw-text-center">
        <p className="tw-text-danger tw-font-medium tw-mb-2">You don’t have permission to view the schedule.</p>
        <p className="tw-text-content-muted tw-text-sm tw-mb-0">
          Your role is not assigned the &quot;View Schedule&quot; permission. If you believe you should have access,
          ask your administrator to assign it to your role in Users → Edit Role, or try logging out and back in
          to refresh your permissions.
        </p>
      </div>
    );
  }

  if (loadError === 'error') {
    return (
      <div className="tw-p-4 tw-text-center">
        <p className="tw-text-danger tw-font-medium tw-mb-2">Failed to load schedule.</p>
        <p className="tw-text-content-muted tw-text-sm tw-mb-0">Please try again or contact support if the problem continues.</p>
      </div>
    );
  }

  // Print handler is defined above so hooks are unconditional.
  return (
    <div
      className="schedule-gantt-wrapper tw-grow tw-flex tw-flex-col min-h-0 tw-border tw-border-line tw-rounded tw-bg-surface-alt tw-relative"
      onDragOver={(ev) => {
        if (fillerActivities.length > 0) {
          ev.preventDefault();
          ev.dataTransfer.dropEffect = 'copy';

          const apiState = (ganttApi as { getState?: () => { focusCell?: { row?: number | string } } } | null)?.getState?.();
          const focusRowId = apiState?.focusCell?.row;
          const fromFocus = typeof focusRowId === 'string' ? Number(focusRowId) : Number(focusRowId);
          if (Number.isFinite(fromFocus)) {
            dragOverRowIdRef.current = fromFocus;
            return;
          }

          const fromPoint = detectSyntheticRowIdAtPoint(ev.clientX, ev.clientY);
          if (fromPoint != null) dragOverRowIdRef.current = fromPoint;
        }
      }}
      onDrop={handleFillerDrop}
      onDragLeave={() => {
        dragOverRowIdRef.current = null;
      }}
    >
      {showFillerPanel && fillerActivities.length > 0 ? (
        <div
          style={{
            position: 'absolute',
            top: 56,
            right: 10,
            zIndex: 40,
            width: 230,
            padding: 10,
            borderRadius: 8,
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.10)',
            background: 'rgba(255, 255, 255, 0.96)',
            pointerEvents: 'auto',
          }}
        >
          <div className="tw-text-sm tw-font-semibold tw-mb-2">Filler Activity</div>
          {fillerActivities.map((activity) => {
            const dragPayload = JSON.stringify({
              activityId: activity.activityId,
              activityName: activity.activityName,
              activityNumber: activity.activityNumber,
            });
            return (
              <div
                key={`${activity.activityId}-${activity.activityNumber}`}
                draggable={hasForemanAreaFilter && !isCreatingFillerTask}
                onDragStart={(ev) => {
                  if (!hasForemanAreaFilter) {
                    ev.preventDefault();
                    setFillerDropError('Please apply a Foreman Area filter before dragging a filler activity.');
                    return;
                  }
                  setFillerDropError(null);
                  setIsDraggingFiller(true);
                  ev.dataTransfer.setData('application/x-filler-activity', dragPayload);
                  ev.dataTransfer.setData('text/plain', dragPayload);
                  ev.dataTransfer.effectAllowed = 'copy';
                }}
                onDragEnd={() => setIsDraggingFiller(false)}
                style={{
                  cursor: isCreatingFillerTask || !hasForemanAreaFilter ? 'not-allowed' : 'grab',
                  border: '1px solid #ced4da',
                  borderRadius: 6,
                  padding: '8px 10px',
                  background: hasForemanAreaFilter ? '#f8f9fa' : '#eceff3',
                  opacity: hasForemanAreaFilter ? 1 : 0.8,
                }}
                title="Drag onto the schedule to create a 1-day planned filler task"
              >
                <div className="tw-text-sm tw-font-medium">{activity.activityName}</div>
                <div className="tw-text-sm tw-text-content-muted">#{activity.activityNumber}</div>
              </div>
            );
          })}
          {fillerDropError ? (
            <div className="tw-text-danger tw-text-sm tw-mt-2">{fillerDropError}</div>
          ) : !hasForemanAreaFilter ? (
            <div className="tw-text-warning-text tw-text-sm tw-mt-2">Select a Foreman Area filter to enable drag & drop.</div>
          ) : (
            <div className="tw-text-content-muted tw-text-sm tw-mt-2">Drag and drop onto a row to create as planned.</div>
          )}
        </div>
      ) : null}
      <div className="schedule-gantt-scroll-shell">
        <div className="schedule-gantt-viewport schedule-gantt-viewport--hscroll-mirror" ref={viewportRef}>
          <Willow>
            <Tooltip api={ganttApi as import('@svar-ui/react-gantt').IApi | undefined} content={tooltipContent}>
              <Gantt
                init={initGantt}
                tasks={safeTasks}
                links={safeLinks}
                scales={safeScales}
                columns={columns}
                taskTypes={taskTypes}
                start={rangeStart}
                end={rangeEnd}
                autoScale={false}
                cellWidth={SCHEDULE_GANTT_TIMELINE_CELL_WIDTH}
                {...({ markers: todayMarker } as any)}
              />
            </Tooltip>
          </Willow>
        </div>
      </div>
      <div
        className="schedule-gantt-hscroll"
        ref={bottomHScrollRef}
        aria-label="Scroll dates horizontally"
        title="Scroll dates horizontally"
      >
        <div ref={bottomHScrollInnerRef} className="schedule-gantt-hscroll-inner" />
      </div>
      {groupBy === 'vehicle' && equipmentCapacityConflicts.length > 0 ? (
        <Alert variant="danger" className="tw-m-2 tw-py-2">
          <div className="tw-font-semibold">Equipment capacity conflict: we do not have enough vehicle types, please adjust the schedule.</div>
          <div className="tw-text-sm tw-mt-1">
            {equipmentCapacityConflicts
              .slice(0, 6)
              .map((c) => `${c.serviceArea} - ${c.vehicleType}: need ${c.required}, available ${c.available}`)
              .join(' | ')}
            {equipmentCapacityConflicts.length > 6 ? ' ...' : ''}
          </div>
        </Alert>
      ) : null}
      <div className="tw-border-t tw-border-line tw-bg-surface-alt tw-px-2 tw-py-1 tw-text-sm tw-text-content-muted tw-flex tw-justify-between tw-items-center tw-flex-wrap tw-gap-2">
        <span>
          {scalePreset[0].toUpperCase() + scalePreset.slice(1)} view from {dateFrom} to {dateTo}. Bars show start/end of planned work. Expand a{" "}
          {groupBy === 'vehicle' ? 'vehicle' : 'people'} row (arrow) to see Planned/Active tasks. Bar colours and the today line are explained in the chart key on the schedule page. Tasks labeled [CONFLICT] need schedule adjustments.
        </span>
        {showConflictWarning ? (
          <Button
            variant="warning"
            size="sm"
            onClick={() => setIsConflictDetailsOpen(true)}
            className="tw-shrink-0"
          >
            Equipment conflicts ({existingConflictNotice?.conflictTaskCount ?? 0})
          </Button>
        ) : null}
      </div>
      <Modal show={pendingConflict != null} onHide={handleAdjustDatesAfterConflict} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Conflict Detected</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingConflict?.resourceConflicts.length ? (
            <Alert variant="warning" className="tw-mb-3">
              People are unavailable for the selected dates. Please select another person or adjust dates.
            </Alert>
          ) : null}
          {pendingConflict?.assignedResourceCount && pendingConflict.assignedResourceCount > 1 ? (
            <Alert variant="danger" className="tw-mb-3">
              This task is assigned to {pendingConflict.assignedResourceCount} resources in the same planning window. Adjust dates to resolve.
            </Alert>
          ) : null}
          {pendingConflict ? (
            <div className="tw-text-sm">
              <div className="tw-mb-2">
                <strong>Task:</strong> {pendingConflict.taskLabel}
              </div>
              <div className="tw-mb-2">
                <strong>Proposed dates:</strong> {pendingConflict.identifiedDateStr} to {pendingConflict.plannedCompletionDateStr}
              </div>
              {pendingConflict.resourceConflicts.length > 0 ? (
                <div>
                  <strong>Conflicting assignments:</strong>
                  <ul className="tw-mb-0 tw-mt-1 tw-pl-3">
                    {pendingConflict.resourceConflicts.slice(0, 8).map((item) => (
                      <li key={`${item.resourceName}-${item.taskId}`}>
                        {item.resourceName}: {item.taskName} ({item.startDate} to {item.endDate})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAdjustDatesAfterConflict}>
            Adjust Dates
          </Button>
          <Button variant="primary" onClick={handleKeepConflictDates}>
            Keep Dates Anyway
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={isConflictDetailsOpen && showConflictWarning} onHide={() => setIsConflictDetailsOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Planning Conflicts Found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="tw-mb-3">
            Equipment availability conflicts were found in the current schedule window.
          </Alert>
          <div className="tw-text-sm">
            <div>
              <strong>Conflicting tasks:</strong> {existingConflictNotice?.conflictTaskCount ?? 0}
            </div>
            <div>
              <strong>Overloaded equipment rows:</strong>{' '}
              {existingConflictNotice?.overloadedRowCount ?? 0}
            </div>
            <div className="tw-mt-2">
              Tasks tagged with <strong>[CONFLICT]</strong> should be adjusted in work planning.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setIsConflictDetailsOpen(false);
              requestAnimationFrame(() => focusFirstConflictRow());
            }}
          >
            Review Conflicts
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResourceGanttChart;
